import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Tag } from 'lucide-react';
import { Reveal } from '@/components/Reveal';

const API = '/api';
const CATEGORIES = ['All', 'Business Strategy', 'Operations', 'Customer Support', 'Healthcare', 'Industry Insights'];

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function SkeletonCard() {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-2xl p-7 animate-pulse">
      <div className="h-3 w-20 bg-[#F3F4F6] rounded mb-4" />
      <div className="h-5 w-4/5 bg-[#F3F4F6] rounded mb-2" />
      <div className="h-5 w-3/5 bg-[#F3F4F6] rounded mb-5" />
      <div className="h-3.5 w-full bg-[#F3F4F6] rounded mb-2" />
      <div className="h-3.5 w-5/6 bg-[#F3F4F6] rounded mb-2" />
      <div className="h-3.5 w-4/5 bg-[#F3F4F6] rounded" />
    </div>
  );
}

function PostCard({ post, featured = false }) {
  return (
    <Link
      to={`/insights/${post.slug}`}
      className={`group flex flex-col h-full bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden hover:border-[#0E5A57]/25 hover:shadow-[0_8px_32px_rgba(14,90,87,0.08)] hover:-translate-y-0.5 transition-all duration-300 ${featured ? 'lg:flex-row' : ''}`}
    >
      {/* Category accent bar */}
      <div className="h-1 bg-gradient-to-r from-[#0E5A57] to-[#2E7D7A] flex-shrink-0" style={{ width: featured ? '' : '100%' }} />

      <div className={`flex flex-col flex-1 p-7 ${featured ? 'lg:p-10' : ''}`}>
        <div className="flex items-center gap-3 mb-5">
          {post.category && (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[#0E5A57] bg-teal-50 border border-teal-100 px-3 py-1 rounded-full font-sans">
              <Tag className="w-3 h-3" /> {post.category}
            </span>
          )}
          {featured && (
            <span className="inline-flex items-center text-xs font-medium text-[#D4AF37] bg-amber-50 border border-amber-100 px-3 py-1 rounded-full font-sans">
              Featured
            </span>
          )}
        </div>

        <h2 className={`font-serif font-bold text-[#153E5C] group-hover:text-[#0E5A57] transition-colors duration-200 leading-snug flex-1 mb-4 ${featured ? 'text-2xl lg:text-3xl' : 'text-lg'}`}>
          {post.title}
        </h2>

        <p className={`text-[#6B7280] font-sans leading-relaxed mb-6 ${featured ? 'text-base' : 'text-sm line-clamp-3'}`}>
          {post.summary}
        </p>

        <div className="flex items-center justify-between mt-auto pt-5 border-t border-[#F3F4F6]">
          <div className="flex items-center gap-1.5 text-xs text-[#9CA3AF] font-sans">
            <Calendar className="w-3 h-3" />
            {formatDate(post.published_at || post.created_at)}
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0E5A57] group-hover:gap-2.5 transition-all duration-200">
            Read article <ArrowRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Insights() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState(false);
  const [category, setCategory] = useState('All');

  useEffect(() => {
    document.title = 'Insights | NKP VENTURES — Business Operations & BPO';
    axios.get(`${API}/blog`)
      .then(r => {
        const data = r.data;
        console.log('[Insights] API response:', data);
        const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : null);
        if (list === null) {
          console.error('[Insights] Unexpected API response shape:', data);
          setApiError(true);
        } else {
          setPosts(list);
        }
      })
      .catch(err => {
        console.error('[Insights] API request failed:', err);
        setApiError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = category === 'All' ? posts : posts.filter(p => p.category === category);
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div>
      {/* ── Hero ── Distinct: white with bold typography + editorial feel */}
      <section className="relative pt-32 pb-20 bg-white border-b border-[#E5E7EB] overflow-hidden">
        {/* Subtle background decor */}
        <div
          className="absolute right-0 top-0 bottom-0 flex items-center pointer-events-none select-none"
          aria-hidden="true"
        >
          <span
            className="font-serif font-bold text-[#F7FAFC] leading-none"
            style={{ fontSize: 'clamp(10rem, 18vw, 18rem)', letterSpacing: '-0.05em' }}
          >
            Insights
          </span>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-2.5 mb-8">
              <span className="w-5 h-px bg-[#0E5A57]" />
              <span className="overline-label text-[#0E5A57]">Insights</span>
            </div>
            <h1 className="font-serif font-bold text-[#153E5C] leading-[1.05] mb-6" style={{ fontSize: 'clamp(2.6rem, 6vw, 4rem)' }}>
              Perspectives on<br />operational excellence.
            </h1>
            <p className="text-[#6B7280] text-lg font-sans leading-relaxed max-w-2xl">
              Practical articles on BPO strategy, operational management, and building scalable international businesses — from NKP VENTURES's editorial team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Category Filter ── */}
      <div className="sticky top-[70px] z-30 bg-white/90 backdrop-blur-sm border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3.5 flex items-center gap-2 overflow-x-auto scrollbar-none">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium flex-shrink-0 transition-all duration-150 ${
                category === cat
                  ? 'bg-[#0E5A57] text-white shadow-sm'
                  : 'bg-[#F7FAFC] text-[#374151] hover:bg-teal-50 hover:text-[#0E5A57] border border-[#E5E7EB]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* ── Posts ── */}
      <section className="py-20 lg:py-28 bg-[#F7FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {loading ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <SkeletonCard />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {[...Array(3)].map((_, i) => <SkeletonCard key={i} />)}
              </div>
            </div>
          ) : apiError ? (
            <div data-testid="insights-api-error" className="text-center py-24">
              <p className="text-red-600 font-sans font-semibold mb-2">Unable to load Insights.</p>
              <p className="text-[#6B7280] font-sans text-sm">Please try again later or contact support if the issue persists.</p>
              <button
                onClick={() => { setApiError(false); setLoading(true); axios.get(`${API}/blog`).then(r => { const d = r.data; setPosts(Array.isArray(d) ? d : (Array.isArray(d?.items) ? d.items : [])); }).catch(() => setApiError(true)).finally(() => setLoading(false)); }}
                className="mt-5 text-sm text-[#0E5A57] font-semibold hover:underline"
              >
                Retry
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="insights-empty" className="text-center py-24">
              <p className="text-[#6B7280] font-sans text-sm">No articles found in this category.</p>
              <button onClick={() => setCategory('All')} className="mt-4 text-sm text-[#0E5A57] font-semibold hover:underline">
                View all articles
              </button>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Featured post */}
              {featured && (
                <Reveal>
                  <PostCard post={featured} featured={true} />
                </Reveal>
              )}

              {/* Grid of remaining posts */}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {rest.map((post, i) => (
                <Reveal key={post.id} delay={i * 0.08}>
                      <PostCard post={post} />
                    </Reveal>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 bg-white border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="overline-label text-[#0E5A57] mb-4">Ready to Improve Your Operations?</p>
                <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#153E5C] leading-tight mb-4">
                  Let's discuss your<br />operational goals.
                </h2>
                <p className="text-[#6B7280] font-sans leading-relaxed max-w-md">
                  Put the ideas in these articles into practice with a dedicated operational team behind you.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                <Link to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#0E5A57] hover:bg-[#2E7D7A] text-white font-bold px-9 py-4 rounded-full transition-all duration-200 group">
                  Schedule a Consultation
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                </Link>
                <Link to="/services"
                  className="inline-flex items-center justify-center gap-2 border border-[#E5E7EB] hover:border-[#0E5A57]/30 text-[#374151] hover:text-[#0E5A57] font-medium px-9 py-4 rounded-full transition-all duration-200 text-sm">
                  Our Services
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
